// Small DOM pieces shared by the automations panel, its editors and the calendar: copy buttons,
// the grouped agent select, the autonomy and output-format controls with their explanation, a
// character counter, code blocks, child-layer tracking and safe localStorage access.
import { h, button, segmented, uid, setText } from '../../dom.js';
import { t } from '../../i18n/fr.js';
import { agentGroups } from './model.js';
import './strings.js';

/**
 * Copies text to the clipboard; toasts the outcome. Never logs the text (it may be a secret).
 * @param {object} ctx app context
 * @param {string} text
 * @returns {Promise<boolean>}
 */
export async function copyText(ctx, text) {
  try {
    if (!globalThis.navigator?.clipboard?.writeText) throw new Error('clipboard');
    await globalThis.navigator.clipboard.writeText(String(text ?? ''));
    ctx.toast?.success?.(t('auto.copied'), { timeout: 2000 });
    return true;
  } catch {
    ctx.toast?.warn?.(t('auto.copyFailed'));
    return false;
  }
}

/** A small "COPIER" button copying getText(). */
export function copyButton(ctx, getText, { label = t('auto.copy'), ariaLabel, variant = 'secondary' } = {}) {
  return button(label, { variant, size: 'sm', icon: 'check', ariaLabel, onClick: () => copyText(ctx, getText()) });
}

/**
 * A code block with a copy button (hook URL, curl example, JSON body).
 * @param {object} ctx
 * @param {string} text
 * @param {{label?: string, multiline?: boolean, copyLabel?: string}} [opts]
 */
export function codeBlock(ctx, text, { label, multiline = false, copyLabel } = {}) {
  const code = h(multiline ? 'pre' : 'code', { class: multiline ? 'auto-code auto-code-block' : 'auto-code' }, text);
  return h('div', { class: 'auto-codewrap' },
    label ? h('p', { class: 'mono-label auto-codelabel' }, label) : null,
    h('div', { class: 'auto-coderow' }, code, copyButton(ctx, () => text, { ariaLabel: copyLabel || (label ? `${t('common.copy')} : ${label}` : t('common.copy')) })));
}

/**
 * A native select of agents grouped by "Étage · Service" (only departments where the user has `need`).
 * @param {object} state app state
 * @param {{need?: string, value?: string, placeholder?: string, id?: string, extra?: object[]}} [opts]
 *   extra: an agent to keep selectable even if outside the groups (editing an item the user sees).
 * @returns {HTMLSelectElement}
 */
export function agentSelect(state, { need = 'manage', value = '', placeholder = t('auto.agentPick'), extra = null } = {}) {
  const el = h('select', { class: 'input select' });
  el.appendChild(h('option', { value: '' }, placeholder));
  const groups = agentGroups(state, need);
  let found = false;
  for (const g of groups) {
    const og = h('optgroup', { label: g.label });
    for (const a of g.agents) {
      if (a.id === value) found = true;
      og.appendChild(h('option', { value: a.id }, `${a.lead ? '★ ' : ''}${a.name}${a.role ? ` — ${a.role}` : ''}`));
    }
    el.appendChild(og);
  }
  if (value && !found && extra) el.appendChild(h('option', { value: extra.id }, extra.name || extra.id));
  el.value = value || '';
  return el;
}

/**
 * The autonomy control: segmented Comme l'agent / Je valide / Autonome + a live explanation.
 * @param {{value?: string, agent?: () => object|null, onChange?: (v: string) => void}} opts
 * @returns {{el: HTMLElement, get value(): string, setValue(v: string): void, refresh(): void}}
 */
export function autonomyControl({ value = 'inherit', agent = () => null, onChange } = {}) {
  const help = h('p', { class: 'field-hint auto-help', 'aria-live': 'polite' });
  const labelId = uid('aut');
  let current = value;
  const seg = segmented([
    { value: 'inherit', label: t('auto.autonomy.inherit') },
    { value: 'approve', label: t('auto.autonomy.approve') },
    { value: 'auto', label: t('auto.autonomy.auto') },
  ], { value, label: t('auto.autonomy'), onChange: (v) => { current = v; refresh(); onChange?.(v); } });
  function refresh() {
    let text;
    if (current === 'inherit') {
      const a = agent();
      text = a && (a.autonomy === 'auto' || a.autonomy === 'approve')
        ? t('auto.autonomyHelp.inherit', { agent: t(`auto.agentAutonomy.${a.autonomy}`) })
        : t('auto.autonomyHelp.inheritUnknown');
    } else text = t(`auto.autonomyHelp.${current}`);
    setText(help, text);
    help.classList.toggle('is-auto', current === 'auto' || (current === 'inherit' && agent()?.autonomy === 'auto'));
  }
  refresh();
  const el = h('div', { class: 'field auto-autonomy', role: 'group', 'aria-labelledby': labelId },
    h('span', { class: 'field-label', id: labelId }, t('auto.autonomy')), seg, help);
  return {
    el,
    get value() { return current; },
    setValue(v) { current = v; seg.setValue(v); refresh(); },
    refresh,
  };
}

/**
 * The output format control (Texte / JSON) with its explanation.
 * @returns {{el: HTMLElement, get value(): string, setValue(v: string): void}}
 */
export function formatControl({ value = 'text', onChange } = {}) {
  const help = h('p', { class: 'field-hint' });
  const labelId = uid('fmt');
  let current = value;
  const seg = segmented([
    { value: 'text', label: t('auto.format.text') },
    { value: 'json', label: t('auto.format.json') },
  ], { value, label: t('auto.format'), onChange: (v) => { current = v; refresh(); onChange?.(v); } });
  function refresh() { setText(help, t(`auto.formatHelp.${current}`)); }
  refresh();
  const el = h('div', { class: 'field auto-format', role: 'group', 'aria-labelledby': labelId },
    h('span', { class: 'field-label', id: labelId }, t('auto.format')), seg, help);
  return {
    el,
    get value() { return current; },
    setValue(v) { current = v; seg.setValue(v); refresh(); },
  };
}

/**
 * A live "n / max" counter for an input or textarea.
 * @param {HTMLInputElement|HTMLTextAreaElement} control
 * @param {number} max
 */
export function counter(control, max) {
  const el = h('span', { class: 'auto-counter', 'aria-hidden': 'true' });
  const sync = () => {
    const n = String(control.value || '').length;
    setText(el, t('auto.counter', { n, max }));
    el.classList.toggle('is-over', n > max);
  };
  control.addEventListener('input', sync);
  sync();
  el.sync = sync;
  return el;
}

/**
 * Keeps the modals opened from a panel in check: they are closed when the panel closes (a route
 * change closes only the routed layer, not what was stacked on it).
 * @param {{onClose(fn: Function): Function}} parent
 * @returns {{add<T>(layer: T): T, closeAll(reason?: string): void}}
 */
export function childLayers(parent) {
  const set = new Set();
  const closeAll = (reason = 'route') => {
    for (const l of [...set]) { try { l.close(reason); } catch { /* already closed */ } }
    set.clear();
  };
  parent?.onClose?.(() => closeAll('route'));
  return {
    add(layer) {
      if (!layer) return layer;
      set.add(layer);
      layer.onClose?.(() => set.delete(layer));
      return layer;
    },
    closeAll,
  };
}

/**
 * localStorage prefix of the sample payloads of the trigger editor (`<prefix><triggerId|_new>`).
 * A sample may be a real n8n payload with customer data: it is forgotten when the trigger is
 * deleted and at every logout / session end (clearTriggerSamples).
 */
export const TRIGGER_SAMPLE_PREFIX = 'mo.trigger.sample.';

/**
 * Remove every trigger sample payload stored in this browser. Never throws.
 * @param {Storage} [storage]
 * @returns {number} keys removed
 */
export function clearTriggerSamples(storage = globalThis.localStorage) {
  try {
    if (!storage) return 0;
    const keys = [];
    for (let i = 0; i < storage.length; i += 1) {
      const k = storage.key(i);
      if (typeof k === 'string' && k.startsWith(TRIGGER_SAMPLE_PREFIX)) keys.push(k);
    }
    for (const k of keys) storage.removeItem(k);
    return keys.length;
  } catch {
    return 0;
  }
}

/** localStorage read that never throws (private mode, blocked storage). */
export function storageGet(key) {
  try { return globalThis.localStorage?.getItem(key) ?? null; } catch { return null; }
}

/** localStorage write that never throws; null removes the key. */
export function storageSet(key, value) {
  try {
    if (value == null) globalThis.localStorage?.removeItem(key);
    else globalThis.localStorage?.setItem(key, value);
  } catch { /* ignore */ }
}

/** True on phone-sized screens (< 768 px). */
export function isMobile() {
  try { return !!globalThis.matchMedia?.('(max-width: 767px)').matches; } catch { return false; }
}
